import React, { useState } from 'react';
import { Breakpoints, intl, Tool, useBreakpoint } from 'ehs-react';
import AuthenticationWizardOtpConfirmationPage from './AuthenticationWizardOtpConfirmationPage';
import AuthenticationWizardSetPasswordPage from './AuthenticationWizardSetPasswordPage';
import AuthenticationWizardUpdateUserData from './AuthenticationWizardUpdateUserData';
import AuthenticationWizardActivateAccountPage from './AuthenticationWizardActivateAccount';
import { usePortletStore } from '../App';
import { SecondaryBackButton } from './SecondaryBackButton';
import { AppModes } from '../util/AppModeUtil';

enum AuthenticationStep {
  ConfirmCode = 'confirmCode',
  UpdateUserData = 'updateUserData',
  SetPassword = 'setPassword',
  ActivateAccount = 'activateAccount'
}

function AuthenticationWizard({
  onWizardExecuted,
  onBackClicked
}: Readonly<{
  onWizardExecuted: () => void;
  onBackClicked?: () => void;
}>): React.JSX.Element {
  const [step, setStep] = useState(AuthenticationStep.ConfirmCode);
  const [
    updateUserDataAllowed,
    updatePasswordAllowed,
    appMode
  ] = usePortletStore(state => [
    state.configuration.updateUserDataAllowed,
    state.configuration.updatePasswordAllowed,
    state.configuration.appMode
  ]);

  const setNextStep = (step: AuthenticationStep): void => {
    if (!updatePasswordAllowed && step == AuthenticationStep.SetPassword) {
      step = AuthenticationStep.ActivateAccount;
    }
    setStep(step);
  };
  const getHeaderText = (step: AuthenticationStep) => {
    if (step === AuthenticationStep.SetPassword) {
      return intl(
        'patientSelfOnboardingPortlet.authenticationWizard.stepSetPassword'
      );
    } else if (appMode === AppModes.PasswordReset) {
      return intl(
        'patientSelfOnboardingPortlet.authenticationWizard.resetPassword.title'
      );
    } else {
      return intl(
        'patientSelfOnboardingPortlet.authenticationWizard.activateAccountTitle'
      );
    }
  };
  const isMobileView = useBreakpoint(Breakpoints.MOBILE);
  const canGoBack = step !== AuthenticationStep.ActivateAccount;
  return (
    <>
      {
        <div className="authentication-header">
          {isMobileView && onBackClicked && canGoBack && (
            <Tool
              icon="arrow-left"
              onClick={onBackClicked}
              tooltip={intl('view.button.back')}
            />
          )}
          <h1 className="authentication-header-text font-18 center sh-font">
            {getHeaderText(step)}
          </h1>
        </div>
      }
      <div className="authentication-content s-font">
        {step === AuthenticationStep.ConfirmCode && (
          <div className="authentication-content-step-content step-confirm-code">
            <div className="authentication-content-image-container">
              <img
                src="/o/PatientSelfOnboardingPortlet/images/authentication_wizard_otp.svg"
                role="none"
              />
            </div>
            <h2 className="font-22 sh-font">
              {intl(
                'patientSelfOnboardingPortlet.authenticationWizard.stepConfirmation'
              )}
            </h2>
            <AuthenticationWizardOtpConfirmationPage
              onNextPage={() =>
                setNextStep(
                  updateUserDataAllowed
                    ? AuthenticationStep.UpdateUserData
                    : AuthenticationStep.SetPassword
                )
              }
            />
          </div>
        )}
        {step === AuthenticationStep.UpdateUserData && (
          <div className="authentication-content-step-content step-update-userdata">
            <div className="authentication-content-image-container">
              <img
                src="/o/PatientSelfOnboardingPortlet/images/complete_user_account.svg"
                role="none"
              />
            </div>
            <h2 className="font-22 sh-font">
              {intl(
                'patientSelfOnboardingPortlet.authenticationWizard.stepUpdateUserData'
              )}
            </h2>
            <AuthenticationWizardUpdateUserData
              onNextPage={() => setNextStep(AuthenticationStep.SetPassword)}
            />
          </div>
        )}
        {step === AuthenticationStep.SetPassword && (
          <div className="authentication-content-step-content step-set-password">
            <div className="authentication-content-image-container">
              <img
                src="/o/PatientSelfOnboardingPortlet/images/authentication_wizard_password.svg"
                role="none"
              />
            </div>
            <h2 className="font-22 sh-font">
              {intl(
                'patientSelfOnboardingPortlet.authenticationWizard.stepSetPassword'
              )}
            </h2>
            <AuthenticationWizardSetPasswordPage
              onNextPage={() => setNextStep(AuthenticationStep.ActivateAccount)}
            />
          </div>
        )}
        {step === AuthenticationStep.ActivateAccount && (
          <div className="authentication-content-step-content step-activate-account">
            <div className="authentication-content-image-container">
              <img
                src="/o/PatientSelfOnboardingPortlet/images/complete_user_account.svg"
                role="none"
              />
            </div>
            <AuthenticationWizardActivateAccountPage
              onNextPage={() => onWizardExecuted()}
            />
          </div>
        )}
        {!isMobileView && onBackClicked && canGoBack && (
          <SecondaryBackButton onBackClicked={onBackClicked} />
        )}
      </div>
    </>
  );
}

export default AuthenticationWizard;
